"use client";
// @ts-strict-ignore

import { Fragment, useEffect, useState } from "react";
import { ThemeProvider } from "styled-components";
import lightTheme from "themes/light";
import { Link } from "i18n/routing";
import Button from "components/parts/button";
import {
  BannerText,
  CancelButton,
  LabelEmphasis,
  ModalContent,
  ModalFooter,
  ModalHeader,
  PrivacyLinkText,
  Section
} from "./styles";
import CheckBox from "components/parts/Checkbox";
import { enableAnalyticsTracking } from "analytics/index";
import HoverUnderline from "components/parts/HoverUnderline";
import { Body } from "components/parts/Typography";
import { Trans } from "i18n/Trans";
import { useTranslations } from "i18n";
import { FILES } from "i18n/constants";
import { Dialog } from "@hubble/web";
import { useCookieSettings } from "hooks/cookies/useCookieSettings";
import darkTheme from "themes/dark";

const NecessaryCookiesLabel = () => (
  <Fragment>
    <Trans
      i18nKey={`${FILES.COMMON}.necessaryCookies`}
      components={[<LabelEmphasis />]}
    />
  </Fragment>
);

const AnalyticsCookiesLabel = () => (
  <Fragment>
    <Trans
      i18nKey={`${FILES.COMMON}.analyticsCookies`}
      components={[<LabelEmphasis />]}
    />
  </Fragment>
);

const getGPC = () => {
  return (
    typeof navigator !== "undefined" &&
    "globalPrivacyControl" in navigator &&
    (navigator.globalPrivacyControl as boolean)
  );
};

const CookieSettingsModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const t = useTranslations(FILES.COMMON);
  const { cookieSettings, setCookieSettings } = useCookieSettings();

  const [formAllowAnalytics, setFormAllowAnalyticsCookies] = useState(
    cookieSettings.allowAnalytics
  );

  const onCookieSettingsSave = () => {
    setCookieSettings({
      hasDismissed: true,
      allowAnalytics: formAllowAnalytics
    });
    if (!cookieSettings.allowAnalytics && formAllowAnalytics) {
      enableAnalyticsTracking();
    }
  };

  const onModalCancel = () => {
    setFormAllowAnalyticsCookies(cookieSettings.allowAnalytics);
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <Dialog.Container>
        <ThemeProvider theme={lightTheme}>
          <Dialog.Header>
            <ModalHeader>{t("manageCookies")}</ModalHeader>
          </Dialog.Header>
          <Dialog.Content>
            <ModalContent>
              <Section>
                <CheckBox
                  name="necessaryCookies"
                  onChange={() => {}}
                  disabled
                  value={true}
                  label={<NecessaryCookiesLabel />}
                />
              </Section>
              <Section>
                <CheckBox
                  name="analyticsCookies"
                  label={<AnalyticsCookiesLabel />}
                  onChange={() => setFormAllowAnalyticsCookies((v) => !v)}
                  value={formAllowAnalytics}
                />
              </Section>
            </ModalContent>
          </Dialog.Content>
          <Dialog.Footer>
            <ModalFooter>
              <Dialog.Close asChild>
                <CancelButton onClick={onModalCancel}>
                  {t("cancel")}
                </CancelButton>
              </Dialog.Close>

              <Dialog.Close asChild>
                <Button onClick={onCookieSettingsSave} isSmall isPrimary>
                  {t("save")}
                </Button>
              </Dialog.Close>
            </ModalFooter>
          </Dialog.Footer>
        </ThemeProvider>
      </Dialog.Container>
    </Dialog>
  );
};

const CookieAgreement = () => {
  const t = useTranslations(FILES.COMMON);
  const [gpc] = useState(getGPC());
  const {
    cookieSettings: { hasDismissed },
    setCookieSettings
  } = useCookieSettings();
  const showAgreement = !gpc && !hasDismissed;

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (gpc) {
      setCookieSettings({
        allowAnalytics: false,
        hasDismissed: true,
        enableOptOutCcpa: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpc]);

  const dismissBanner = () => {
    setCookieSettings({
      hasDismissed: true
    });
  };

  return showAgreement ? (
    <ThemeProvider theme={darkTheme}>
      <section
        id="cookiePolicyAgreement"
        className="fixed bottom-6 left-1/2 z-[100] box-border w-[90%] -translate-x-1/2 rounded-[5px] border border-white bg-[#010304] p-6 md:right-6 md:left-auto md:w-[428px] md:translate-x-0"
      >
        <BannerText>
          <Trans
            i18nKey={`${FILES.COMMON}.cookieBanner`}
            components={[
              <Link id="cookie-privacyPolicy-1" href="/legal/privacy-policy" />,
              <PrivacyLinkText />
            ]}
          />
        </BannerText>
        <div className="flex items-center">
          <Button
            isDarkTheme={darkTheme.isDark}
            isLight={!darkTheme.isDark}
            isSmall
            css={{ width: "90px", height: "40px", marginRight: "24px" }}
            onClick={dismissBanner}
          >
            {t("gotIt")}
          </Button>
          <Button
            css={{
              background: "transparent",
              border: "none",
              outline: "none",
              height: "40px",
              minWidth: "fit-content",
              padding: "0px",
              width: "fit-content"
            }}
            isSmall
            onClick={() => setIsOpen(true)}
          >
            <Body size="sm" semiBold sm={{ m: "0px" }}>
              <span>
                <HoverUnderline>{t("manage")}</HoverUnderline>
              </span>
            </Body>
          </Button>
        </div>
      </section>
      <CookieSettingsModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </ThemeProvider>
  ) : null;
};

export default CookieAgreement;
